import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Loader2 } from "lucide-react";

export default function AddFileDialog({ open, onOpenChange, clientId, onCreated }) {
    const [form, setForm] = useState({ name: "", size: "", link: "", note: "" });
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const { data } = await api.post(`/clients/${clientId}/files`, form);
            toast.success("File added");
            setForm({ name: "", size: "", link: "", note: "" });
            onCreated?.(data);
        } catch (err) {
            toast.error("Could not add file", { description: formatApiErrorDetail(err.response?.data?.detail) });
        } finally { setLoading(false); }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[480px] rounded-xl border-slate-200">
                <DialogHeader>
                    <DialogTitle className="font-display text-2xl tracking-tight">Add a file</DialogTitle>
                    <DialogDescription className="text-slate-500">
                        Add a file record or an external link. Real uploads arrive with Google Drive.
                    </DialogDescription>
                </DialogHeader>
                <form onSubmit={submit} className="space-y-4 pt-2">
                    <div className="space-y-2">
                        <Label className="text-slate-700">Name</Label>
                        <Input required value={form.name}
                               onChange={(e) => setForm({ ...form, name: e.target.value })}
                               data-testid="file-name" className="ring-terra"
                               placeholder="brand-guidelines.pdf" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-2">
                            <Label className="text-slate-700">Size (optional)</Label>
                            <Input value={form.size}
                                   onChange={(e) => setForm({ ...form, size: e.target.value })}
                                   data-testid="file-size" className="ring-terra"
                                   placeholder="2.4 MB" />
                        </div>
                        <div className="space-y-2">
                            <Label className="text-slate-700">Link (optional)</Label>
                            <Input type="url" value={form.link}
                                   onChange={(e) => setForm({ ...form, link: e.target.value })}
                                   data-testid="file-link" className="ring-terra"
                                   placeholder="https://…" />
                        </div>
                    </div>
                    <div className="space-y-2">
                        <Label className="text-slate-700">Note (optional)</Label>
                        <Textarea rows={2} value={form.note}
                                  onChange={(e) => setForm({ ...form, note: e.target.value })}
                                  data-testid="file-note" className="ring-terra" />
                    </div>
                    <div className="flex items-center justify-end gap-2 pt-2">
                        <button type="button" onClick={() => onOpenChange(false)}
                                data-testid="file-cancel"
                                className="px-4 py-2 rounded-lg text-sm font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-50 transition-colors">
                            Cancel
                        </button>
                        <button type="submit" disabled={loading}
                                data-testid="file-submit"
                                className="btn-terra rounded-lg px-4 py-2 text-sm font-medium inline-flex items-center gap-2 disabled:opacity-70">
                            {loading && <Loader2 className="w-4 h-4 animate-spin" />} Add file
                        </button>
                    </div>
                </form>
            </DialogContent>
        </Dialog>
    );
}
